import {
  DEFAULT_FOCUS_TEMPLATE,
  DEFAULT_ROUTINE_HABIT_PACK,
  DEFAULT_STANDALONE_PACK
} from 'assets/data';
import { HabitPackFormat, Mode, PackAndFocusEntityType, PackEditorMode, PackEditorTabMode } from 'enums';
import {
  FocusModeTemplateType,
  HabitPackType
} from 'interfaces/common.interface';
import { SettingsType } from 'interfaces/setting.interface';
import { useCallback } from 'react';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { store } from 'store';
import {
  setCurrentViewedFocusTemplate,
  updateInitialCurrentViewedFocusTemplate
} from 'store/reducer/focus-mode/slice';
import {
  closePackAndFocusEditor,
  closePackPreview,
  openPackAndFocusEditor,
  setCurrentViewedPack,
  updateInitialCurrentViewedPack,
  updateIsHabitPackAllowedToBeEdited
} from 'store/reducer/habit-pack/slice';
import {
  changeCurrentSettings,
  configurePackEditorTabs,
  configureTabs,
  updatePreviousSettings,
  resetActiveTabIndex,
  updateIsSettingEditingFinished
} from 'store/reducer/setting/slice';
import { cloneDeep } from 'utils/helpers/cloneDeep';
import { getApiLanguageCode } from 'utils/helpers';
import { hasPackEditorUnsavedChanges } from 'utils/pack-editor.util';
import { v4 as uuid } from 'uuid';
import { OpenPackAndFocusEditorParams } from 'interfaces/marketplace.interface';
import { packAndFocusEditorHookSelector } from 'store/reducer/selectors/pack-and-focus-editor.selector';

const resolveHabitPackTabMode = (
  params: Extract<
    OpenPackAndFocusEditorParams,
    { type: PackAndFocusEntityType.HABIT_PACK }
  >
): PackEditorTabMode => {
  if (params.mode === PackEditorMode.CREATE) {
    return params.packType === HabitPackFormat.ROUTINE
      ? PackEditorTabMode.ROUTINE
      : PackEditorTabMode.STANDALONE;
  }
  return params.pack.pack_type === HabitPackFormat.ROUTINE
    ? PackEditorTabMode.ROUTINE
    : PackEditorTabMode.STANDALONE;
};

export const usePackAndFocusEditor = () => {
  const dispatch = useAppDispatch();
  const {
    isPackAndFocusEditorOpen,
    editorType,
    editorMode,
    packEditorTabMode,
    currentSettings,
    initialCurrentViewedPack,
    initialCurrentViewedFocusTemplate,
    currentViewedFocusTemplate
  } = useAppSelectorShallow(packAndFocusEditorHookSelector);

  const closeEditor = useCallback(() => {
    const { habitPack, focusMode } = store.getState();
    const snapshot = habitPack.userSettingsSnapshot;

    if (snapshot) {
      dispatch(changeCurrentSettings(cloneDeep(snapshot.currentSettings)));
      dispatch(updatePreviousSettings(cloneDeep(snapshot.previousSettings)));
    }

    if (habitPack.focusTemplateSnapshot) {
      dispatch(
        setCurrentViewedFocusTemplate(
          cloneDeep(habitPack.focusTemplateSnapshot)
        )
      );
    }
    if (habitPack.initialFocusTemplateSnapshot) {
      dispatch(
        updateInitialCurrentViewedFocusTemplate(
          cloneDeep(habitPack.initialFocusTemplateSnapshot)
        )
      );
    } else if (focusMode.initialCurrentViewedFocusTemplate) {
      dispatch(
        updateInitialCurrentViewedFocusTemplate(
          cloneDeep(focusMode.initialCurrentViewedFocusTemplate)
        )
      );
    }

    dispatch(configureTabs({ mode: Mode.DEFAULT }));
    dispatch(updateIsSettingEditingFinished(false));
    dispatch(closePackAndFocusEditor());
  }, [dispatch]);

  const openEditor = useCallback(
    (params: OpenPackAndFocusEditorParams) => {
      dispatch(closePackPreview());
      const rootState = store.getState();
      const {
        currentSettings: current,
        previousSettings: previous,
        language: lang
      } = rootState.setting;
      const apiLanguage = getApiLanguageCode(lang);

      const userSettingsSnapshot = {
        currentSettings: cloneDeep(current),
        previousSettings: cloneDeep(previous)
      };

      const focusTemplateSnapshot = cloneDeep(
        rootState.focusMode.currentViewedFocusTemplate
      );
      const initialFocusTemplateSnapshot = cloneDeep(
        rootState.focusMode.initialCurrentViewedFocusTemplate
      );

      dispatch(resetActiveTabIndex());
      dispatch(updateIsSettingEditingFinished(false));
      dispatch(updateIsHabitPackAllowedToBeEdited(true));

      if (params.type === PackAndFocusEntityType.HABIT_PACK) {
        const packTabMode = resolveHabitPackTabMode(params);

        let packToUse: SettingsType;
        if (params.mode === PackEditorMode.CREATE) {
          packToUse =
            params.packType === HabitPackFormat.ROUTINE
              ? {
                  ...DEFAULT_ROUTINE_HABIT_PACK,
                  id: uuid(),
                  language: apiLanguage
                }
              : {
                  ...DEFAULT_STANDALONE_PACK,
                  id: uuid(),
                  language: apiLanguage
                };
        } else {
          packToUse = { ...params.pack, language: apiLanguage };
        }

        dispatch(
          openPackAndFocusEditor({
            editorType: PackAndFocusEntityType.HABIT_PACK,
            editorMode: params.mode,
            packEditorTabMode: packTabMode,
            userSettingsSnapshot,
            focusTemplateSnapshot,
            initialFocusTemplateSnapshot
          })
        );
        dispatch(configurePackEditorTabs(packTabMode));
        dispatch(changeCurrentSettings(packToUse));
        dispatch(updateInitialCurrentViewedPack(packToUse));
        dispatch(setCurrentViewedPack(packToUse as HabitPackType));
      } else {
        const template: FocusModeTemplateType =
          params.mode === PackEditorMode.CREATE
            ? { ...DEFAULT_FOCUS_TEMPLATE, language: apiLanguage }
            : { ...params.template, language: apiLanguage };

        dispatch(
          openPackAndFocusEditor({
            editorType: PackAndFocusEntityType.FOCUS_TEMPLATE,
            editorMode: params.mode,
            packEditorTabMode: PackEditorTabMode.FOCUS_MODE,
            userSettingsSnapshot,
            focusTemplateSnapshot,
            initialFocusTemplateSnapshot
          })
        );
        dispatch(setCurrentViewedFocusTemplate(template));
        dispatch(updateInitialCurrentViewedFocusTemplate(template));
        dispatch(configurePackEditorTabs(PackEditorTabMode.FOCUS_MODE));
        dispatch(changeCurrentSettings({ ...template, language: apiLanguage }));
      }
    },
    [dispatch]
  );

  const isEditorDirty = useCallback(() => {
    if (!isPackAndFocusEditorOpen) {
      return false;
    }
    return hasPackEditorUnsavedChanges({
      packEditorTabMode,
      currentSettings,
      initialCurrentViewedPack,
      initialCurrentViewedFocusTemplate,
      currentViewedFocusTemplate
    });
  }, [
    isPackAndFocusEditorOpen,
    packEditorTabMode,
    currentSettings,
    initialCurrentViewedPack,
    initialCurrentViewedFocusTemplate,
    currentViewedFocusTemplate
  ]);

  return {
    isPackAndFocusEditorOpen,
    editorType,
    editorMode,
    packEditorTabMode,
    openEditor,
    closeEditor,
    isEditorDirty
  };
};
